﻿import { offset } from '@floating-ui/dom';
import { autoUpdate, flip, FloatingPortal, shift, useDismiss, useFloating, useInteractions, useTransitionStyles } from '@floating-ui/react';
import { Reveal } from '@progress/kendo-react-animation';
import { Button } from '@progress/kendo-react-buttons';
import { Checkbox, FlatColorPicker } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router';
import { UNIVERSAL_MAP_COLOR_PALETTE_SETTINGS } from '../../../../../common/color-picker-palette';
import useFilterGroup from '../../../../../hooks/use-filter-group';
import { ChevronDownOutline, ChevronRightOutline, RefreshOutline } from '../../../../../icons/icons';
import { UDViewStaticDataSubLayerWithColor } from '../../../../../udView/udview';
import { useAppDispatch } from '../../../../hooks/store-hooks';
import useMap from '../../../../hooks/use-map';
import { ROUTES } from '../../../../router/routes';
import { patchFocusedWorkspaceView } from '../../../../state/store';
import { SEARCH_PARAM_FILTER_GROUP_TYPE } from '../../../settings/components/sub-pages/filter-groups/SettingsFilterGroups';
import { SETTINGS_ROUTES } from '../../../settings/router/settings-routes';
import { useWorkspaceView } from '../../hooks/use-view';
import { LayerConfig, LayerConfigDefaultColor, LayerFieldConfig, LayersNavState } from './LayersFlyout.types';

export function LayersSection(props: {
    id?: string;
    layerConfig: LayerConfig;
    onNavigate: (state: LayersNavState) => void;
    children?: React.ReactNode;
}) {
    const { layerConfig } = props;
    const [menuExpanded, setMenuExpanded] = useState(false);
    const [filterGroupMenuExpanded, setFilterGroupMenuExpanded] = useState(false);
    const filterGroupConfig = layerConfig.filterGroups;
    const filterGroups = useFilterGroup(filterGroupConfig?.type);
    const filterGroupView = useWorkspaceView((x) => filterGroupConfig?.get(x));
    const defaultColors = useDefaultColors(props.layerConfig.defaultColor);
    const dispatch = useAppDispatch();

    return (
        <div className={'d-flex flex-column justify-content-between layers-section'} id={props.id}>
            <div className={'d-flex justify-content-between align-items-center gap-2'}>
                <div className={'d-flex align-items-center'}>
                    {layerConfig.fields || filterGroupConfig || props.children ? (
                        <Button
                            svgIcon={menuExpanded ? ChevronDownOutline : ChevronRightOutline}
                            fillMode={'flat'}
                            className="button-small menu-button layers-expand"
                            onClick={() => setMenuExpanded(!menuExpanded)}></Button>
                    ) : (
                        <div className={'menu-button'}></div>
                    )}
                    <Checkbox label={layerConfig.label} checked={layerConfig.value} onChange={layerConfig.onChange}></Checkbox>
                </div>
                {layerConfig.navigateTo ? (
                    <Button
                        svgIcon={ChevronRightOutline}
                        fillMode={'flat'}
                        className={clsx('button-small', 'menu-button', 'section-expand-btn')}
                        onClick={() => props.onNavigate(layerConfig.navigateTo!)}></Button>
                ) : (
                    <></>
                )}
            </div>
            <Reveal>
                {menuExpanded && (
                    <div>
                        {props.children}
                        {layerConfig.fields && <Fields fields={layerConfig.fields} defaultColors={defaultColors} />}
                        {filterGroupView && filterGroupConfig && (
                            <div className={'menu-item'}>
                                <div className="nest-1">
                                    <Button
                                        svgIcon={filterGroupMenuExpanded ? ChevronDownOutline : ChevronRightOutline}
                                        fillMode={'flat'}
                                        className={'button-small menu-button float-end'}
                                        onClick={() => setFilterGroupMenuExpanded(!filterGroupMenuExpanded)}
                                    />
                                </div>
                                <Checkbox
                                    label={'Filter groups'}
                                    checked={filterGroupView.show}
                                    onChange={(e) =>
                                        dispatch(patchFocusedWorkspaceView((x) => (filterGroupConfig.get(x).show = e.value)))
                                    }></Checkbox>
                            </div>
                        )}
                    </div>
                )}
            </Reveal>
            <Reveal>
                {filterGroupMenuExpanded && menuExpanded && filterGroupView && filterGroupConfig && (
                    <div style={{ paddingLeft: '2rem' }}>
                        {filterGroups.map((group) => (
                            <div key={group.id} className="menu-item d-flex align-items-center">
                                <div className={'menu-button'}></div>
                                <Checkbox
                                    label={group.name}
                                    checked={filterGroupView.ids.includes(group.id)}
                                    onChange={() => {
                                        dispatch(
                                            patchFocusedWorkspaceView((x) => {
                                                const items = filterGroupConfig.get(x).ids;
                                                const idx = items.indexOf(group.id);
                                                if (idx !== -1) {
                                                    items.splice(idx, 1);
                                                } else {
                                                    items.push(group.id);
                                                }
                                            })
                                        );
                                    }}></Checkbox>
                                <div className="flex-grow-1"></div>
                                {filterGroupConfig.colored && (
                                    <ColorPicker
                                        markerStyle="cross"
                                        color={filterGroupView.config[group.id]?.color}
                                        onChange={(e) =>
                                            dispatch(
                                                patchFocusedWorkspaceView((x) => {
                                                    const item = filterGroupConfig.get(x);
                                                    if (group.id in item.config) {
                                                        item.config[group.id]!.color = e.value;
                                                    } else {
                                                        item.config[group.id] = { color: e.value };
                                                    }
                                                })
                                            )
                                        }
                                    />
                                )}
                            </div>
                        ))}
                        {filterGroups.length === 0 && (
                            <div style={{ paddingLeft: '2rem' }}>
                                <div className={'secondary-text'}>No filter groups found.</div>
                                <div className={'secondary-text'}>
                                    <span>You can set them </span>
                                    <NavLink
                                        to={`${ROUTES.SETTINGS}/${SETTINGS_ROUTES.filterGroups.index}/${SETTINGS_ROUTES.filterGroups.manage}?${SEARCH_PARAM_FILTER_GROUP_TYPE}=${layerConfig.filterGroups?.type}`}>
                                        here
                                    </NavLink>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Reveal>
        </div>
    );
}

function Fields(props: { fields: LayerFieldConfig[]; nestLevel?: number; defaultColors: string[] | null }) {
    const nestLevel = props.nestLevel ?? 1;
    return props.fields.map((field, index) => (
        <Field nestLevel={nestLevel} field={field} key={index} defaultColors={props.defaultColors} />
    ));
}

function Field({
    field,
    nestLevel,
    defaultColors: defaultColor
}: {
    field: LayerFieldConfig;
    nestLevel: number;
    defaultColors: string[] | null;
}) {
    const [expanded, setExpanded] = useState(false);
    const fieldDefaultColor = useDefaultColors(field.defaultColor);
    const value = field.value;
    const hasColorPicker = typeof value === 'object';
    const checked = typeof field.value === 'boolean' ? field.value : field.value.show;

    return (
        <div className={clsx({ 'mt-1': field.spacingBefore })}>
            <div className="menu-item">
                <div className={`nest-${nestLevel}`}>
                    {field.fields && (
                        <Button
                            svgIcon={expanded ? ChevronDownOutline : ChevronRightOutline}
                            fillMode="flat"
                            className="button-small menu-button layers-expand float-end"
                            onClick={() => setExpanded(!expanded)}
                        />
                    )}
                </div>
                <div className="d-flex align-items-center justify-content-between">
                    <Checkbox label={field.label} checked={checked} onChange={(e) => field.setChecked(e.value)}></Checkbox>
                    {hasColorPicker && <FieldColorPicker field={field} defaultColors={fieldDefaultColor ?? defaultColor} />}
                </div>
            </div>

            <Reveal>
                {expanded && (
                    <div>
                        <Fields nestLevel={nestLevel + 1} fields={field.fields!} defaultColors={defaultColor} />
                    </div>
                )}
            </Reveal>
        </div>
    );
}

function FieldColorPicker(props: { field: LayerFieldConfig; defaultColors: string[] | null }) {
    const field = props.field as LayerFieldConfig & { value: UDViewStaticDataSubLayerWithColor };
    const selectedColor = field.value.color;

    return <ColorPicker color={selectedColor} defaultColors={props.defaultColors} onChange={(e) => field.setColor?.(e.value)} />;
}

function useDefaultColors(defaultColor: LayerConfigDefaultColor | undefined): string[] | null {
    const map = useMap();

    const [color, setColor] = useState<string[] | null>(null);

    useEffect(() => {
        // No default color
        if (!defaultColor) {
            return;
        }

        const setColorFromGlobalState = () => {
            if (!map) {
                return;
            }

            const globalStateValue = map.getGlobalState();
            setColor(
                defaultColor
                    .map((x) => {
                        if (x.type === 'literal') {
                            return x.color;
                        }

                        const color = globalStateValue[x.value];
                        if (color && typeof color === 'string') {
                            return color;
                        }

                        return null;
                    })
                    .filter(Boolean) as string[]
            );
            if (globalStateValue && typeof globalStateValue === 'string') {
                setColor(globalStateValue);
            }
        };

        setColorFromGlobalState();
        return map?.on('style.load', () => {
            setColorFromGlobalState();
        }).unsubscribe;
    }, [map, defaultColor]);

    return color;
}

function ColorPicker(props: {
    color?: string | null;
    defaultColors?: string[] | null;
    markerStyle?: 'M' | 'cross';
    onChange: (e: { value: string | null }) => void;
}) {
    const [open, setOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [flip(), shift(), offset({ crossAxis: -20, mainAxis: -15 })],
        placement: 'bottom-end'
    });

    const dismiss = useDismiss(context);

    const { getFloatingProps } = useInteractions([dismiss]);

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: 100,
        initial: (params) => ({
            opacity: 0,
            transform: params.side === 'top' ? 'translateY(50px)' : 'translateY(-50px)'
        })
    });

    const container = useMemo(
        () => (refs.floating?.current as HTMLElement)?.querySelector('.k-coloreditor-footer'),
        [refs.floating.current, isMounted]
    );
    return (
        <>
            <div
                ref={refs.setReference}
                onClick={() => setOpen(true)}
                className="cursor-pointer d-flex align-items-center justify-content-center"
                style={{
                    width: 17,
                    height: 17,
                    background:
                        props.color ??
                        (props.defaultColors?.length === 2
                            ? `linear-gradient(135deg, ${props.defaultColors[0]} 50%, ${props.defaultColors[1]} 50%)`
                            : props.defaultColors?.[0]) ??
                        undefined,
                    borderRadius: 6,
                    position: 'relative',
                    border: '1px solid var(--border-color)'
                }}>
                {!props.color && (
                    <>
                        {props.markerStyle === 'cross' && (
                            <div
                                style={{
                                    left: 0,
                                    right: 0,
                                    height: 1,
                                    background: 'var(--color-red-400)',
                                    position: 'absolute',
                                    rotate: '45deg'
                                }}></div>
                        )}

                        {(!props.markerStyle || props.markerStyle === 'M') && <div style={{ fontSize: '0.6rem', color: 'white' }}>M</div>}
                    </>
                )}
            </div>
            <FloatingPortal>
                {isMounted && (
                    <div ref={refs.setFloating} className="z-2 k-card" style={floatingStyles} {...getFloatingProps()}>
                        <FlatColorPicker
                            activeView="palette"
                            views={['palette', 'gradient']}
                            value={props.color ?? props.defaultColors?.[0] ?? undefined}
                            paletteSettings={UNIVERSAL_MAP_COLOR_PALETTE_SETTINGS}
                            setOpen={setOpen}
                            onChange={(e) => {
                                props.onChange({ value: e.value! });
                                setOpen(false);
                            }}
                        />
                        {container &&
                            createPortal(
                                <Button
                                    className="reset-button button-small"
                                    svgIcon={RefreshOutline}
                                    title="Reset to default"
                                    fillMode="flat"
                                    onClick={() => {
                                        props.onChange({ value: null });
                                        setOpen(false);
                                    }}
                                />,
                                container
                            )}
                    </div>
                )}
            </FloatingPortal>
        </>
    );
}
